import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageCircle, Save, KeyRound, Send, RotateCcw, Zap, Check, X, Edit3, Eye, EyeOff, RefreshCw, Clock, XCircle,
} from "lucide-react";

const PROVIDERS = [
  { value: "mock",       label: "Mock (dev / test)",          hint: "Logs only — nothing sent to WhatsApp. Use during development." },
  { value: "bridge_qr",  label: "In-house Bridge (QR-scan)",   hint: "Scan a QR from your WhatsApp phone once — messages go through your own number. No credentials, no monthly fee. Configure & connect from /admin/whatsapp/bridge." },
  { value: "twilio",     label: "Twilio WhatsApp API",         hint: "Standard SDK provider. Requires Account SID + Auth Token + From number." },
  { value: "gupshup",    label: "Gupshup",                     hint: "Popular in India. Requires API Key + Source phone + App ID." },
  { value: "maytapi",    label: "Maytapi",                     hint: "Requires Product ID + Phone ID + API token." },
  { value: "green",      label: "Green-API",                   hint: "Requires Instance ID + API token." },
  { value: "meta",       label: "Meta / WhatsApp Cloud API",   hint: "Requires Access Token + Phone Number ID." },
  { value: "myapiwhatsapp", label: "myapiwhatsapp.in (Waziper)", hint: "Indian reseller. Requires Instance ID + Access Token from your myapiwhatsapp.in dashboard. Supports text + media." },
  { value: "saasyto",    label: "SaaSyto (WhatsApp Virtual)",    hint: "White-label reseller at web.saasyto.com. Requires Instance ID + Access Token. Uses POST /api/send." },
  { value: "custom",     label: "Custom REST provider",        hint: "Any endpoint. Posts {phone, message, sender} to your base URL." },
];

const FIELD_MAP = {
  twilio:    ["account_sid", "api_token", "from_number"],
  gupshup:   ["api_token", "product_id", "from_number"],
  maytapi:   ["product_id", "phone_id", "api_token"],
  green:     ["instance_id", "api_token", "base_url"],
  meta:      ["api_token", "phone_id", "webhook_verify_token"],
  myapiwhatsapp: ["instance_id", "api_token", "base_url", "from_number"],
  saasyto:   ["instance_id", "api_token", "base_url", "from_number"],
  custom:    ["base_url", "api_token"],
  mock:      [],
  bridge_qr: [],   // No fields — pairing is done via QR at /admin/whatsapp/bridge
};

const FIELD_LABEL = {
  api_token:   { label: "API Token / Secret",     placeholder: "sk_live_… or Bearer token" },
  account_sid: { label: "Account SID (Twilio)",   placeholder: "AC…" },
  phone_id:    { label: "Phone / Phone Number ID", placeholder: "e.g. 1015… (Meta) or 6633… (Maytapi)" },
  product_id:  { label: "Product / App ID",       placeholder: "Gupshup App name or Maytapi Product ID" },
  from_number: { label: "From number / Country code", placeholder: "+919000000000 · or just 91 for myapiwhatsapp" },
  base_url:    { label: "Base URL",               placeholder: "https://myapiwhatsapp.in/api/send (or your provider endpoint)" },
  instance_id: { label: "Instance ID",            placeholder: "myapiwhatsapp / Green-API instance, e.g. 6633XXX" },
  webhook_secret: { label: "Webhook secret (optional)", placeholder: "for delivery-status webhooks" },
  webhook_verify_token: { label: "Webhook Verify Token (Meta)", placeholder: "e.g. sportshub-verify-2026 — you choose this string" },
};

export default function AdminMessageProfile() {
  const [tab, setTab] = useState("provider");
  return (
    <div className="p-5 md:p-8" data-testid="admin-message-profile">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// Messaging</div>
        <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1">Message Profile</h1>
        <p className="text-sm text-white/60 mt-1">Pick a WhatsApp provider, plug credentials, and customise every message template. Test a real send before going live.</p>
      </div>

      <div className="mb-6 border-b border-white/10 flex gap-2 overflow-x-auto">
        <Tab active={tab === "provider"}  onClick={() => setTab("provider")}  testid="msg-tab-provider">
          <KeyRound size={12} /> Provider &amp; API
        </Tab>
        <Tab active={tab === "templates"} onClick={() => setTab("templates")} testid="msg-tab-templates">
          <Edit3 size={12} /> Message Templates
        </Tab>
        <Tab active={tab === "logs"}      onClick={() => setTab("logs")}      testid="msg-tab-logs">
          <MessageCircle size={12} /> Recent Sends
        </Tab>
        <Tab active={tab === "retry"}     onClick={() => setTab("retry")}     testid="msg-tab-retry">
          <RefreshCw size={12} /> OTP Retry Queue
        </Tab>
      </div>

      {tab === "provider"  && <ProviderPanel />}
      {tab === "templates" && <TemplatesPanel />}
      {tab === "logs"      && <LogsPanel />}
      {tab === "retry"     && <RetryQueuePanel />}
    </div>
  );
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 text-[11px] font-mono uppercase tracking-widest border-b-2 flex items-center gap-2 whitespace-nowrap ${
        active ? "border-brand-orange text-white" : "border-transparent text-white/50 hover:text-white"
      }`}>{children}</button>
  );
}

/* --------------------- PROVIDER PANEL --------------------- */
function ProviderPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/admin/whatsapp/config").then((r) => setCfg(r.data));
  }, []);

  if (!cfg) return <div className="text-white/40 py-16 text-center text-xs font-mono uppercase tracking-widest">Loading…</div>;

  const providerMeta = PROVIDERS.find((p) => p.value === cfg.provider) || PROVIDERS[0];
  const activeFields = FIELD_MAP[cfg.provider] || [];

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/whatsapp/config", cfg);
      toast.success("Provider config saved");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Enter a phone number"); return; }
    try {
      const { data } = await api.post("/admin/whatsapp/test-send", {
        phone: testPhone.trim(),
        event: "MEMBERSHIP_PURCHASED",
        params: { member: "Admin Test", plan: "Test Plan", txn: "TEST-1234" },
      });
      toast.success(`Sent: "${data.message?.slice(0, 60) || "(rendered)"}…"`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
  };

  const update = (k, v) => { setCfg({ ...cfg, [k]: v }); setDirty(true); };

  return (
    <div className="max-w-3xl space-y-6" data-testid="provider-panel">
      {/* provider picker */}
      <div className="border border-white/12 bg-[#131A2B] p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Choose provider</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PROVIDERS.map((p) => (
            <label key={p.value}
              className={`flex items-start gap-3 p-3 border cursor-pointer transition ${
                cfg.provider === p.value ? "border-brand-orange bg-brand-orange/8" : "border-white/15 hover:border-white/40"
              }`}>
              <input type="radio" name="prov" checked={cfg.provider === p.value}
                onChange={() => update("provider", p.value)}
                data-testid={`prov-${p.value}`}
                className="mt-1 accent-brand-orange" />
              <div>
                <div className="font-display font-black uppercase text-sm">{p.label}</div>
                <div className="text-[11px] text-white/50 mt-0.5">{p.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* credentials */}
      {activeFields.length > 0 && (
        <div className="border border-white/12 bg-[#131A2B] p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Credentials · {providerMeta.label}</div>
          <div className="space-y-4">
            {activeFields.map((f) => {
              const meta = FIELD_LABEL[f] || { label: f, placeholder: "" };
              const isSecret = f === "api_token" || f === "webhook_secret";
              return (
                <div key={f}>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{meta.label}</label>
                  <input
                    type={isSecret ? "password" : "text"}
                    value={cfg[f] || ""}
                    onChange={(e) => update(f, e.target.value)}
                    placeholder={meta.placeholder}
                    data-testid={`prov-field-${f}`}
                    className="w-full bg-black/40 border border-white/15 focus:border-brand-blue outline-none px-3 py-2.5 text-sm font-mono"
                  />
                </div>
              );
            })}
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Sender name (appears in message header)</label>
              <input value={cfg.sender_name || ""} onChange={(e) => update("sender_name", e.target.value)}
                className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm" data-testid="prov-sender-name" />
            </div>
          </div>
        </div>
      )}

      {cfg.provider === "bridge_qr" && (
        <div className="border-2 border-brand-orange bg-brand-orange/10 p-5" data-testid="bridge-qr-hint">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-2">// In-house WhatsApp Bridge</div>
          <p className="text-sm text-white/80 leading-relaxed">
            No credentials are needed here. Head to the <b>Bridge Console</b> to scan the QR code from your WhatsApp phone
            and get your <b>session ID · phone · API token</b> for connecting other websites too.
          </p>
          <a href="/admin/whatsapp/bridge"
             data-testid="bridge-console-link"
             className="inline-flex mt-3 items-center gap-2 bg-brand-orange text-black px-4 py-2 font-bold text-xs uppercase tracking-widest hover:opacity-90">
            Open Bridge Console →
          </a>
        </div>
      )}

      {cfg.provider === "meta" && (
        <div className="border-2 border-brand-blue bg-brand-blue/8 p-5" data-testid="meta-hint">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Meta WhatsApp Cloud API · setup</div>
          <p className="text-sm text-white/80 leading-relaxed mb-3">
            Meta&apos;s official WhatsApp API. <b>Free up to 1000 conversations / month</b>. Runs on Meta&apos;s servers — nothing to host.
          </p>
          <ol className="text-sm text-white/70 space-y-2 list-decimal pl-5 mb-3">
            <li>
              Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="brand-blue underline">developers.facebook.com/apps</a> → <b>Create App</b> → type: <b>Business</b>
            </li>
            <li>
              In the app → <b>Add Product → WhatsApp → Set Up</b>
            </li>
            <li>
              Copy the two values Meta shows you into the fields above:
              <ul className="list-disc pl-5 mt-1 text-white/60">
                <li><b>API Token</b> — the temporary Access Token (or a permanent System User token from Business Settings)</li>
                <li><b>Phone Number ID</b> — a numeric string next to your test number</li>
              </ul>
            </li>
            <li>
              Pick a <b>Verify Token</b> (any random string — e.g. <code className="bg-black/50 px-1 py-0.5 rounded">sportshub-verify-2026</code>) and paste it in the third field. You&apos;ll paste the same value into Meta&apos;s webhook config.
            </li>
            <li>
              In Meta → WhatsApp → Configuration → Webhooks → <b>Edit</b>:
              <ul className="list-disc pl-5 mt-1">
                <li>Callback URL: <code className="bg-black/50 px-1 py-0.5 rounded font-mono text-xs">https://htpsportshub.in/api/webhooks/meta-whatsapp</code></li>
                <li>Verify Token: paste the same string</li>
                <li>Subscribe to fields: check <b>messages</b></li>
              </ul>
            </li>
            <li>Click <b>Save</b> above, flip <b>Live Mode</b> ON, hit <b>Send Test Message</b> below.</li>
          </ol>
          <div className="text-xs text-white/50">
            Full walkthrough with screenshots: see /admin/whatsapp/campaigns → Meta setup guide (coming with docs deploy)
          </div>
        </div>
      )}

      {cfg.provider === "saasyto" && (
        <div className="border-2 border-sky-500 bg-sky-500/8 p-5" data-testid="saasyto-hint">
          <div className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2">// SaaSyto · setup</div>
          <p className="text-sm text-white/80 leading-relaxed mb-3">
            White-label WhatsApp API from <a href="https://web.saasyto.com" target="_blank" rel="noreferrer" className="text-sky-400 underline">web.saasyto.com</a>. Sends text + media through your paired business WhatsApp using a JSON POST endpoint.
          </p>
          <ol className="text-sm text-white/70 space-y-2 list-decimal pl-5 mb-3">
            <li>
              Login → <b>WhatsApp API</b> → open your <b>Enrolled Phone</b> card.
            </li>
            <li>
              Scan the QR from your business WhatsApp phone (WhatsApp Web-style one-tap pairing).
            </li>
            <li>
              Copy the two values shown:
              <ul className="list-disc pl-5 mt-1 text-white/60">
                <li><b>Instance ID</b></li>
                <li><b>Access Token</b> — paste into <b>API Token / Secret</b> below</li>
              </ul>
            </li>
            <li>
              Leave <b>Base URL</b> empty (defaults to <code className="bg-black/50 px-1 py-0.5 rounded font-mono text-[11px]">https://web.saasyto.com/api/send</code>) unless your dashboard shows a different host.
            </li>
            <li>
              Set <b>country code</b> to <code className="bg-black/50 px-1 py-0.5 rounded">91</code> for auto-prefix on 10-digit Indian numbers.
            </li>
            <li>Save, flip <b>Live Mode</b> ON, hit <b>Send Test Message</b>.</li>
          </ol>
          <div className="text-xs text-white/50">
            If the test fails, open <a href="/admin/whatsapp-logs" className="text-sky-400 underline">WhatsApp Logs</a> — the <b>error</b> column carries the exact SaaSyto response so you can see whether the number wasn&apos;t paired, credits were empty, or the endpoint URL is different.
          </div>
        </div>
      )}

      {cfg.provider === "myapiwhatsapp" && (
        <div className="border-2 border-emerald-500 bg-emerald-500/8 p-5" data-testid="myapiwhatsapp-hint">
          <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-2">// myapiwhatsapp.in · setup</div>
          <p className="text-sm text-white/80 leading-relaxed mb-3">
            Indian WhatsApp reseller (Waziper-based). Sends text + media through your own paired WhatsApp number using a simple GET API.
          </p>
          <ol className="text-sm text-white/70 space-y-2 list-decimal pl-5 mb-3">
            <li>
              Login at <a href="https://myapiwhatsapp.in" target="_blank" rel="noreferrer" className="text-emerald-400 underline">myapiwhatsapp.in</a> → <b>WhatsApp Instances</b> → <b>Create Instance</b>.
            </li>
            <li>
              Scan the shown <b>QR code</b> from your business WhatsApp phone (same one-tap pairing as WhatsApp Web).
            </li>
            <li>
              Copy the two values shown on the instance card:
              <ul className="list-disc pl-5 mt-1 text-white/60">
                <li><b>Instance ID</b> — e.g. <code className="bg-black/50 px-1 py-0.5 rounded font-mono text-[11px]">6633ABC1D2E3F</code></li>
                <li><b>Access Token</b> — the API Token / Secret above</li>
              </ul>
            </li>
            <li>
              Leave <b>Base URL</b> empty (auto-defaults to <code className="bg-black/50 px-1 py-0.5 rounded font-mono text-[11px]">https://myapiwhatsapp.in/api/send</code>) unless your dashboard shows a different host.
            </li>
            <li>
              Set the <b>country code</b> to <code className="bg-black/50 px-1 py-0.5 rounded">91</code> so 10-digit Indian numbers auto-prefix.
            </li>
            <li>Click <b>Save</b> above, flip <b>Live Mode</b> ON, hit <b>Send Test Message</b> below to confirm delivery.</li>
          </ol>
          <div className="text-xs text-white/50">
            Delivery-receipt webhooks (optional): point their dashboard to <code className="bg-black/50 px-1 py-0.5 rounded font-mono text-[11px]">https://htpsportshub.in/api/webhooks/myapiwhatsapp</code> — will land in <b>WhatsApp Logs</b> as delivered/read.
          </div>
        </div>
      )}

      {/* Fallback provider — auto-retry when primary fails */}
      <FallbackPanel
        fallback={cfg.fallback || { provider: "mock", is_live: false }}
        onChange={(fb) => { setCfg({ ...cfg, fallback: fb }); setDirty(true); }}
        primaryProvider={cfg.provider}
      />

      {/* live toggle */}
      <div className={`border ${cfg.is_live ? "border-green-500/60 bg-green-950/20" : "border-yellow-500/50 bg-yellow-950/10"} p-5 flex items-center justify-between gap-4`}>
        <div>
          <div className="font-display font-black uppercase text-sm flex items-center gap-2">
            {cfg.is_live ? <Zap size={16} className="text-green-400" /> : <Zap size={16} className="text-yellow-400" />}
            {cfg.is_live ? "Live mode — real messages will be sent" : "Test mode — messages will only be logged"}
          </div>
          <div className="text-xs text-white/60 mt-1">
            {cfg.is_live
              ? "Every WhatsApp trigger from the platform will call your provider's API."
              : "Turn this on when your credentials are verified & tested."}
          </div>
        </div>
        <button
          onClick={() => update("is_live", !cfg.is_live)}
          data-testid="prov-is-live-toggle"
          className={`px-4 py-2.5 font-bold text-xs uppercase tracking-widest border ${
            cfg.is_live ? "bg-green-500 text-white border-green-500" : "border-white/20 text-white/80 hover:border-yellow-500"
          }`}
        >
          {cfg.is_live ? "LIVE" : "GO LIVE"}
        </button>
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={busy || !dirty} data-testid="prov-save-btn"
          className="bg-brand-orange text-black px-5 py-3 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-40 inline-flex items-center gap-2">
          <Save size={12} /> {busy ? "Saving…" : "Save Provider"}
        </button>
      </div>

      {/* test send */}
      <div className="border border-white/12 bg-[#131A2B] p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Send a test message</div>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+91 9xxxxxxxxx"
            data-testid="prov-test-phone"
            className="flex-1 bg-black/40 border border-white/15 px-3 py-2.5 text-sm" />
          <button onClick={sendTest} data-testid="prov-test-send"
            className="bg-brand-blue text-white px-5 py-2.5 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 justify-center">
            <Send size={12} /> Send Test
          </button>
        </div>
        <div className="mt-2 text-[11px] text-white/40">
          Uses the <span className="brand-orange font-bold">MEMBERSHIP_PURCHASED</span> template. Sample params are substituted automatically.
        </div>
      </div>
    </div>
  );
}

/* --------------------- FALLBACK PANEL --------------------- */
function FallbackPanel({ fallback, onChange, primaryProvider }) {
  const fb = fallback || { provider: "mock", is_live: false };
  const active = fb.provider && fb.provider !== "mock";
  const providerMeta = PROVIDERS.find((p) => p.value === fb.provider) || PROVIDERS[0];
  const activeFields = FIELD_MAP[fb.provider] || [];

  const setField = (k, v) => onChange({ ...fb, [k]: v });

  return (
    <div className={`border-2 ${active ? "border-purple-500/60 bg-purple-950/10" : "border-white/12 bg-[#131A2B]"} p-5`} data-testid="fallback-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-purple-400">// Auto-failover</div>
          <div className="font-display text-lg font-black uppercase mt-0.5">Fallback provider</div>
          <p className="text-xs text-white/60 mt-1 max-w-lg">
            When the primary <b className="brand-orange">{primaryProvider}</b> returns a 5xx / rate-limit / credits-empty, we&apos;ll auto-retry the same message once through this fallback so critical OTPs and payment confirmations never miss.
          </p>
        </div>
        <label className="flex items-center gap-2 shrink-0 mt-1">
          <input type="checkbox" checked={!!fb.is_live} onChange={(e) => setField("is_live", e.target.checked)}
            data-testid="fb-is-live" className="accent-purple-500" />
          <span className="text-[11px] font-mono uppercase tracking-widest">
            {fb.is_live ? "Enabled" : "Disabled"}
          </span>
        </label>
      </div>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Fallback provider</label>
        <select value={fb.provider || "mock"} onChange={(e) => setField("provider", e.target.value)}
          data-testid="fb-provider" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm">
          {PROVIDERS.map((p) => (
            <option key={p.value} value={p.value} disabled={p.value === primaryProvider}>
              {p.label} {p.value === primaryProvider ? "(same as primary — pick a different one)" : ""}
            </option>
          ))}
        </select>
      </div>

      {active && activeFields.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-purple-400 mb-3">// Fallback credentials · {providerMeta.label}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeFields.map((f) => {
              const meta = FIELD_LABEL[f] || { label: f, placeholder: "" };
              const isSecret = f === "api_token" || f === "webhook_secret";
              return (
                <div key={f} className={f === "webhook_verify_token" || f === "base_url" ? "md:col-span-2" : ""}>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">{meta.label}</label>
                  <input
                    type={isSecret ? "password" : "text"}
                    value={fb[f] || ""}
                    onChange={(e) => setField(f, e.target.value)}
                    placeholder={meta.placeholder}
                    data-testid={`fb-field-${f}`}
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {active && (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-purple-300">
          On failure: <b className="brand-orange">{primaryProvider}</b> → <b>{fb.provider}</b>. Each attempt is logged in WhatsApp Logs with <code className="bg-black/50 px-1 rounded">primary_status</code> / <code className="bg-black/50 px-1 rounded">fallback_status</code>.
        </div>
      )}
    </div>
  );
}

/* --------------------- TEMPLATES PANEL --------------------- */
function TemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get("/admin/whatsapp/templates").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const save = async (t) => {
    try {
      await api.post("/admin/whatsapp/templates", {
        event: t.event, template: t.template, enabled: t.enabled, description: t.description,
      });
      toast.success(`Template "${t.event}" saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const reset = async (event) => {
    if (!window.confirm("Reset to default template?")) return;
    try {
      await api.post(`/admin/whatsapp/templates/reset/${event}`);
      toast.success("Template reset");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="space-y-3 max-w-4xl" data-testid="templates-panel">
      {loading ? <div className="text-white/40 py-16 text-center text-xs font-mono uppercase tracking-widest">Loading…</div> :
        items.map((t, i) => (
          <TemplateCard key={t.event} row={t} onChange={(v) => {
            const next = [...items]; next[i] = { ...next[i], ...v }; setItems(next);
          }} onSave={save} onReset={reset} />
        ))
      }
    </div>
  );
}

function TemplateCard({ row, onChange, onSave, onReset }) {
  return (
    <div className="border border-white/12 bg-[#131A2B] p-5" data-testid={`tpl-card-${row.event}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{row.event}</div>
          <div className="text-sm text-white/60 mt-0.5">{row.description}</div>
        </div>
        <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest">
          <input type="checkbox" checked={row.enabled !== false} onChange={(e) => onChange({ enabled: e.target.checked })}
            data-testid={`tpl-enabled-${row.event}`}
            className="accent-brand-orange" />
          <span className={row.enabled === false ? "text-red-400" : "text-green-400"}>
            {row.enabled === false ? <><EyeOff size={11} className="inline mr-1" />Off</> : <><Eye size={11} className="inline mr-1" />On</>}
          </span>
        </label>
      </div>
      <textarea rows={3} value={row.template} onChange={(e) => onChange({ template: e.target.value })}
        data-testid={`tpl-text-${row.event}`}
        className="w-full bg-black/40 border border-white/15 focus:border-brand-blue outline-none px-3 py-2 text-sm font-mono" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-[10px] font-mono text-white/40">
          Placeholders like <span className="brand-blue">{"{member}"}</span>, <span className="brand-blue">{"{plan}"}</span> etc. are substituted at send time.
        </div>
        <div className="flex gap-2">
          <button onClick={() => onReset(row.event)} data-testid={`tpl-reset-${row.event}`}
            className="border border-white/15 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest hover:border-yellow-500 inline-flex items-center gap-1.5">
            <RotateCcw size={11} /> Reset
          </button>
          <button onClick={() => onSave(row)} data-testid={`tpl-save-${row.event}`}
            className="bg-brand-orange text-black px-4 py-1.5 font-bold text-[11px] uppercase tracking-widest hover:bg-white inline-flex items-center gap-1.5">
            <Save size={11} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------- LOGS PANEL --------------------- */
function LogsPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/whatsapp/logs").then((r) => setLogs(r.data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-white/40 py-16 text-center text-xs font-mono uppercase tracking-widest">Loading…</div>;
  return (
    <div className="border border-white/10 overflow-x-auto max-w-5xl" data-testid="logs-panel">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-white/5">
          <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
            <th className="px-4 py-3">When</th>
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">Event</th>
            <th className="px-4 py-3">To</th>
            <th className="px-4 py-3">Rendered</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-white/6">
              <td className="px-4 py-3 font-mono text-xs text-white/70">{new Date(l.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 text-xs">{l.provider}</td>
              <td className="px-4 py-3 text-xs brand-orange font-mono">{l.template}</td>
              <td className="px-4 py-3 font-mono text-xs">{l.phone}</td>
              <td className="px-4 py-3 text-[12px] text-white/70 max-w-md truncate" title={l.rendered}>{l.rendered}</td>
              <td className="px-4 py-3">
                {l.status === "sent" ? <span className="text-[10px] font-mono uppercase text-green-400 border border-green-500/50 px-2 py-0.5">SENT</span> :
                 l.status === "failed" ? <span className="text-[10px] font-mono uppercase text-red-400 border border-red-500/50 px-2 py-0.5" title={l.error}>FAILED</span> :
                 <span className="text-[10px] font-mono uppercase text-yellow-400 border border-yellow-500/50 px-2 py-0.5">{l.status}</span>}
              </td>
            </tr>
          ))}
          {logs.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-10 text-center text-white/40 text-sm">No WhatsApp messages sent yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------- RETRY QUEUE PANEL --------------------- */
function RetryQueuePanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");    // "", "queued", "sent", "exhausted", "cancelled"
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/whatsapp/retry-queue" + (filter ? `?status=${filter}` : ""));
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const tick = async () => {
    try { await api.post("/admin/whatsapp/retry-queue/tick"); toast.success("Retry sweep triggered"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Tick failed"); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this queued retry?")) return;
    try { await api.post(`/admin/whatsapp/retry-queue/${id}/cancel`); toast.success("Cancelled"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const badge = (s) => ({
    queued:    "border-yellow-500/50 text-yellow-300 bg-yellow-500/10",
    sent:      "border-green-500/50 text-green-300 bg-green-500/10",
    exhausted: "border-red-500/50 text-red-300 bg-red-500/10",
    cancelled: "border-white/30 text-white/50 bg-white/5",
  }[s] || "border-white/20 text-white/60");

  return (
    <div className="max-w-6xl" data-testid="retry-queue-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// OTP retry queue</div>
          <p className="text-xs text-white/60 mt-1 max-w-xl">
            When BOTH primary and fallback WhatsApp providers fail on a critical message (OTPs, payment confirmations), it lands here and self-heals via 1→2→5→10→30 min retries.
          </p>
        </div>
        <button onClick={tick} data-testid="retry-tick-now"
          className="inline-flex items-center gap-2 bg-brand-orange text-black px-3 py-2 font-bold text-[11px] uppercase tracking-widest hover:bg-white">
          <RefreshCw size={12} /> Run sweep now
        </button>
      </div>

      <div className="flex gap-2 mb-3 text-[11px] font-mono uppercase tracking-widest">
        {["", "queued", "sent", "exhausted", "cancelled"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`retry-filter-${s || "all"}`}
            className={`px-3 py-1.5 border ${filter === s ? "border-brand-orange text-white bg-brand-orange/10" : "border-white/15 text-white/60 hover:text-white"}`}>
            {s || "all"} {counts[s] ? `· ${counts[s]}` : ""}
          </button>
        ))}
      </div>

      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5">
            <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
              <th className="p-2">Template</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Attempts</th>
              <th className="p-2">Next attempt</th>
              <th className="p-2">Status</th>
              <th className="p-2">Last error</th>
              <th className="p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40 text-sm">
                Nothing in the retry queue right now — that&apos;s a good thing. Failed OTPs would land here automatically.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-white/8" data-testid={`retry-row-${r.id}`}>
                <td className="p-2 font-mono">{r.template}</td>
                <td className="p-2 font-mono">{r.phone}</td>
                <td className="p-2">{r.attempts} / {r.max_attempts}</td>
                <td className="p-2 font-mono text-[10px] text-white/60">
                  {r.status === "queued" && r.next_attempt_at
                    ? <span className="inline-flex items-center gap-1"><Clock size={10} /> {new Date(r.next_attempt_at).toLocaleTimeString("en-IN")}</span>
                    : "—"}
                </td>
                <td className="p-2">
                  <span className={`px-1.5 py-0.5 border ${badge(r.status)} text-[10px] font-mono uppercase tracking-widest`}>
                    {r.status}
                  </span>
                </td>
                <td className="p-2 text-white/60 truncate max-w-[240px]" title={r.last_error || ""}>{r.last_error || "-"}</td>
                <td className="p-2 text-right">
                  {r.status === "queued" && (
                    <button onClick={() => cancel(r.id)} data-testid={`retry-cancel-${r.id}`}
                      className="inline-flex items-center gap-1 border border-red-500/40 text-red-300 hover:bg-red-500/20 px-2 py-1 text-[10px] font-mono uppercase tracking-widest">
                      <XCircle size={10} /> Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

